// Audit readiness and the audit pack (Marius's audit boards 7, 8 and 10, 1 Nov
// 2026): a seventh Administration tab "Audit" (/p/:projectId/admin/audit) for
// the project manager, built on the same evidence the auditor link opens
// (apps/api/src/modules/stakeholders/lib/audit-evidence.ts).
import { z } from "zod";
import { isoMonth } from "../schemas/common";

/**
 * The areas of proof, in the board's order. "setup": the project's open setup
 * items (onboarding openItems, Task 0.4), for projects set up through the
 * onboarding; it has gaps but no area card.
 */
export const AUDIT_AREAS = ["costs", "expert_days", "activities", "results", "changes", "reports", "setup"] as const;
export type AuditArea = (typeof AUDIT_AREAS)[number];
export const AUDIT_AREA_LABELS: Record<AuditArea, string> = {
  costs: "Costs",
  expert_days: "Expert days",
  activities: "Activities",
  results: "Results",
  changes: "Changes",
  reports: "Reports",
  setup: "Project setup",
};

/** One kind of proof in an area: how many of the records that need it have it ("179 of 186"). */
export type AuditProofRow = { key: string; label: string; done: number; of: number };

/** An area's card: "Costs and receipts · 186 costs · € 1.128.400", its proof rows and how many are missing. */
export type AuditAreaCard = { key: AuditArea; title: string; sub: string; rows: AuditProofRow[]; missing: number };

/** Who can fix a gap: a person of the team (user) or an expert. */
export type AuditOwner = { kind: "user" | "expert"; id: string; name: string };

/** One gap: the record it is on, what is missing, who can fix it and where. */
export type AuditGap = {
  /** Stable: "<area>:<kind>:<record id>"; the anchor the "Missing for audit" tags link to. */
  key: string;
  area: AuditArea;
  /** The tag: "Receipt", "Proof of payment", "Timesheet", "Attendance list", … */
  kind: string;
  text: string;
  owner: AuditOwner | null;
  fix: { label: string; path: string };
  /** The record the gap is on (an expense, an activity, …; a timesheet as "<engagement id>:<month>"). */
  entity: { type: string; id: string };
};

export type AuditOpened = { key: string; what: string; link: string; at: string };

export type AuditReadiness = {
  /** The whole project: its start and end (null when not set). */
  span: { from: string | null; to: string | null };
  /** Proof in place of proof needed, whole project. */
  inPlace: number;
  needed: number;
  /** 0 to 100, rounded down (100 only when nothing is missing). */
  pct: number;
  /** The newest auditor link that is not switched off. */
  link: { state: "active" | "expired" | "none"; label: string | null; until: string | null };
  /** How often the auditor links were opened (team previews do not count), and when last. */
  opened: { count: number; last: string | null };
  gapCount: number;
  ownerCount: number;
  areas: AuditAreaCard[];
  gaps: AuditGap[];
  /** What was opened through the auditor links, newest first (at most 20). */
  log: AuditOpened[];
  /** The contract lead (or an owner/admin when none) may send reminders and make packs. */
  canAct: boolean;
};

/** The gaps on one record (the "Missing for audit" tag on an expense, timesheet or event page). */
export type AuditGapsOf = { gaps: AuditGap[] };

export type AuditRemindersResult = { sent: number; withoutEmail: number };

// ---------- the audit pack (board 8) ----------

export const AUDIT_PACK_SORTS = ["budget_line", "date"] as const;
export type AuditPackSort = (typeof AUDIT_PACK_SORTS)[number];
export const AUDIT_PACK_SORT_LABELS: Record<AuditPackSort, string> = { budget_line: "Budget line", date: "Date" };

export const auditPackInclude = z.object({
  costs: z.boolean(),
  expertDays: z.boolean(),
  activities: z.boolean(),
  photos: z.boolean(),
  missing: z.boolean(),
});
export type AuditPackInclude = z.infer<typeof auditPackInclude>;

export const auditPackBody = z
  .object({
    from: isoMonth,
    to: isoMonth,
    sort: z.enum(AUDIT_PACK_SORTS),
    include: auditPackInclude,
  })
  .refine((b) => b.from <= b.to, { message: "The end month comes after the start month.", path: ["to"] });
export type AuditPackBody = z.infer<typeof auditPackBody>;

/** A folder or file of the pack, as the preview shows it ("03_Costs_by_budget_line/ · 186 costs · 702 files"). */
export type AuditPackNode = { name: string; folder: boolean; note: string; children: AuditPackNode[] };

export type AuditPackPreview = {
  root: string;
  /** "1,2 GB · 1.304 files" (files the app makes are counted, their size is not known before). */
  note: string;
  tree: AuditPackNode[];
};

export type AuditPackStatus = "preparing" | "ready" | "failed" | "expired";
export type AuditPackRow = {
  id: string;
  /** "Sep 2025 to Oct 2026 · without photos" */
  label: string;
  by: string;
  createdAt: string;
  size: string | null;
  status: AuditPackStatus;
  /** Where it downloads (relative to /api/p/:projectId) while it is ready and not expired. */
  download: string | null;
};

export type AuditPackView = {
  packs: AuditPackRow[];
  /** The months the pack can cover: the project's months up to this one ("2025-09"). */
  months: string[];
  /** "Adds about 600 MB" */
  photos: string;
  canAct: boolean;
};

/** How long a pack's download works. */
export const AUDIT_PACK_DAYS = 7;
